import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  User,
  Phone,
  Shield,
  Bell,
  Edit,
  Save
} from 'lucide-react';

export default function DashboardProfileTab({
  editingProfile,
  setEditingProfile,
  profile,
  setProfile,
  commsPrefs,
  setCommsPrefs,
  handleSaveProfile,
  handleSaveCommsPrefs
}) {
  return (
    <div className="space-y-6">
      {/* Personal Info */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-secondary" />
              Personal Information
            </CardTitle>
            <CardDescription>Your travel profile information used for bookings</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => editingProfile ? handleSaveProfile() : setEditingProfile(true)}
          >
            {editingProfile ? (
              <><Save className="h-4 w-4 mr-1" /> Save</>
            ) : (
              <><Edit className="h-4 w-4 mr-1" /> Edit</>
            )}
          </Button>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Full Name</Label>
            <Input
              value={profile.fullName}
              onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
              disabled={!editingProfile}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input value={profile.email} disabled />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Phone</Label>
            <Input
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              disabled={!editingProfile}
              placeholder="Your phone number"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Date of Birth</Label>
            <Input
              type="date"
              value={profile.dateOfBirth}
              onChange={(e) => setProfile({ ...profile, dateOfBirth: e.target.value })}
              disabled={!editingProfile}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Passport Number</Label>
            <Input
              value={profile.passportNumber}
              onChange={(e) => setProfile({ ...profile, passportNumber: e.target.value })}
              disabled={!editingProfile}
              placeholder="Passport number"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Passport Expiry</Label>
            <Input
              type="date"
              value={profile.passportExpiry}
              onChange={(e) => setProfile({ ...profile, passportExpiry: e.target.value })}
              disabled={!editingProfile}
            />
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-secondary" />
            Emergency Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Contact Name</Label>
            <Input
              value={profile.emergencyName}
              onChange={(e) => setProfile({ ...profile, emergencyName: e.target.value })}
              disabled={!editingProfile}
              placeholder="Emergency contact name"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Contact Phone</Label>
            <Input
              value={profile.emergencyPhone}
              onChange={(e) => setProfile({ ...profile, emergencyPhone: e.target.value })}
              disabled={!editingProfile}
              placeholder="Emergency contact phone"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Relationship</Label>
            <Input
              value={profile.emergencyRelationship}
              onChange={(e) => setProfile({ ...profile, emergencyRelationship: e.target.value })}
              disabled={!editingProfile}
              placeholder="e.g., Spouse, Parent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Health & Dietary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-secondary" />
            Health & Dietary Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Dietary Restrictions</Label>
            <Input
              value={profile.dietaryRestrictions}
              onChange={(e) => setProfile({ ...profile, dietaryRestrictions: e.target.value })}
              disabled={!editingProfile}
              placeholder="e.g., Vegetarian, Halal, Gluten-free"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Allergies</Label>
            <Input
              value={profile.allergies}
              onChange={(e) => setProfile({ ...profile, allergies: e.target.value })}
              disabled={!editingProfile}
              placeholder="e.g., Peanuts, Shellfish"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Medical Conditions</Label>
            <Textarea
              value={profile.medicalConditions}
              onChange={(e) => setProfile({ ...profile, medicalConditions: e.target.value })}
              disabled={!editingProfile}
              placeholder="Any medical conditions we should be aware of"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Communication Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-secondary" />
            Communication Preferences
          </CardTitle>
          <CardDescription>Control what emails and notifications you receive</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Payment Reminders</p>
              <p className="text-xs text-muted-foreground">Reminders for upcoming payment due dates</p>
            </div>
            <Switch
              checked={commsPrefs.emailPaymentReminders}
              onCheckedChange={(v) => setCommsPrefs({ ...commsPrefs, emailPaymentReminders: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Departure Reminders</p>
              <p className="text-xs text-muted-foreground">Countdown and prep reminders before your trip</p>
            </div>
            <Switch
              checked={commsPrefs.emailDepartureReminders}
              onCheckedChange={(v) => setCommsPrefs({ ...commsPrefs, emailDepartureReminders: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Trip Updates</p>
              <p className="text-xs text-muted-foreground">Important updates about your booked trips</p>
            </div>
            <Switch
              checked={commsPrefs.emailTripUpdates}
              onCheckedChange={(v) => setCommsPrefs({ ...commsPrefs, emailTripUpdates: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Match Notifications</p>
              <p className="text-xs text-muted-foreground">When someone wants to connect as a travel partner</p>
            </div>
            <Switch
              checked={commsPrefs.emailMatchNotifications}
              onCheckedChange={(v) => setCommsPrefs({ ...commsPrefs, emailMatchNotifications: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Newsletter & Promotions</p>
              <p className="text-xs text-muted-foreground">Travel inspiration, deals, and new trip announcements</p>
            </div>
            <Switch
              checked={commsPrefs.emailNewsletter}
              onCheckedChange={(v) => setCommsPrefs({ ...commsPrefs, emailNewsletter: v })}
            />
          </div>
          
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-primary mb-3">Privacy Settings</h4>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Allow Direct Messages</p>
                <p className="text-xs text-muted-foreground">Let other travelers send you private messages</p>
              </div>
              <Switch
                checked={commsPrefs.allowDirectMessages !== false}
                onCheckedChange={(v) => setCommsPrefs({ ...commsPrefs, allowDirectMessages: v })}
              />
            </div>
          </div>
          
          <Button variant="outline" className="mt-4" onClick={handleSaveCommsPrefs}>
            Save Preferences
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
